import React, { forwardRef } from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import type { BaseInputProps } from './shared';
import { inputBaseClasses, inputErrorClasses } from './shared';

export interface TextAreaInputProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'>, BaseInputProps {
  /** Nombre de lignes visibles */
  rows?: number;
}

export const TextAreaInput = forwardRef<HTMLTextAreaElement, TextAreaInputProps>(
  (
    {
      label,
      id,
      errors = [],
      helpText,
      containerClassName = '',
      className = '',
      rows = 3,
      disabled,
      readOnly,
      required,
      ...props
    },
    ref
  ) => {
    const hasErrors = errors.length > 0;
    const inputId = id || `textarea-input-${Math.random().toString(36).slice(2)}`;

    const textareaClasses = [
      inputBaseClasses,
      'resize-y min-h-[80px]',
      hasErrors ? inputErrorClasses : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={`space-y-token-xs ${containerClassName}`}>
        {label && (
          <label htmlFor={inputId} className="block type-caption-text font-medium text-ink">
            {label}
            {required && <span className="text-token-danger ml-1">*</span>}
          </label>
        )}
        <textarea
          ref={ref}
          id={inputId}
          rows={rows}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          aria-invalid={hasErrors}
          aria-describedby={
            hasErrors ? `${inputId}-errors` : helpText ? `${inputId}-help` : undefined
          }
          aria-label={label || props['aria-label']}
          className={textareaClasses}
          {...props}
        />
        {hasErrors && (
          <div id={`${inputId}-errors`} className="space-y-1" role="alert">
            {errors.map((error, index) => (
              <p
                key={index}
                className="type-caption-text text-token-danger flex items-center gap-1"
              >
                <ExclamationCircleIcon className="h-4 w-4 flex-shrink-0" />
                {error}
              </p>
            ))}
          </div>
        )}
        {helpText && !hasErrors && (
          <p id={`${inputId}-help`} className="type-caption-text text-muted">
            {helpText}
          </p>
        )}
      </div>
    );
  }
);

TextAreaInput.displayName = 'TextAreaInput';
